import { ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output, inject, DestroyRef } from '@angular/core';
import { Subscription, switchMap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FirBasketMaintenanceService } from './fir-basket-maintenance.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-fir-basket-maintenance',
    templateUrl: './fir-basket-maintenance.component.html',
    styleUrls: ['./fir-basket-maintenance.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FirBasketMaintenanceComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private readonly contextSubs = new Subscription();
  private lookupSub?: Subscription;
  private isInitialized = false;
  private isLoadingLookups = false;

  baseURLs: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};
  USER_SETTINGS: any = {};

  minimise = false;
  modalTitle = 'Add New Basket';
  loading = false;
  isSaveClicked = false;

  basketName = '';
  selectedSource: any = null;
  selectedFtc: any = null;
  selectedPassive: any = null;
  selectedSubf: any = null;
  selectedFphci: any = null;
  selected902Name: any = null;

  sourceOptions: any[] = [];
  ftcOptions: any[] = [];
  passiveOptions: any[] = [];
  subfOptions: any[] = [];
  fphciOptions: any[] = [];
  name902Options: any[] = [];

  constructor(
    private service: FirBasketMaintenanceService,
    private snack: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) {}

  ngOnInit(): void {
    this.refreshFromWorkspace();
    this.contextSubs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => this.refreshFromWorkspace()),
    );
    this.contextSubs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => this.refreshFromWorkspace()),
    );
  }

  ngOnDestroy(): void {
    this.lookupSub?.unsubscribe();
    this.contextSubs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const hydrated = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    let fp = normalizeFilterParams(activeScreen);
    if (!Object.keys(fp).length) {
      fp = extractObject(hydrated);
    }
    this.filterParams = fp;
    const mergedGp = { ...extractObject(shell.globalParams), ...extractObject(hydrated) };
    this.globalService = {
      ...shell,
      globalParams: mergedGp,
      filterParams: fp,
      workspaceFactory: this.gtwWorkspace.workspaceFactory,
      getScenario: (ty: any) => this.gtwGlobal.getScenario(ty),
      getEIN: () => this.gtwGlobal.getEIN(),
      getTAXYEAR: () => this.gtwGlobal.getTAXYEAR(),
      getChartType: () => this.gtwGlobal.getChartType(),
      inputs: this.gtwGlobal.inputs,
      sso_id: this.gtwApp?.USER_SETTINGS?.user?.sso_id ?? '',
    };
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = { ...gc, base_url: gc.base_url || gc.api || '' };
    this.baseURLs = gc;
    this.USER_SETTINGS = this.gtwApp.USER_SETTINGS || {};
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow)
      ? extractObject(rawRow[0]?.data ?? rawRow[0])
      : extractObject(rawRow);
  }

  private refreshFromWorkspace(): void {
    this.lookupSub?.unsubscribe();
    this.lookupSub = undefined;
    this.isInitialized = false;
    this.isLoadingLookups = false;
    this.applyWorkspaceContext();
    this.tryInit();
    this.cdr.markForCheck();
  }

  private tryInit(): void {
    if (this.isInitialized || this.isLoadingLookups) {
      return;
    }
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api;
    if (!baseUrl) {
      return;
    }
    this.isInitialized = true;
    this.loadLookups();
  }

  private get taxYear(): any {
    const gp = extractObject(this.globalService?.globalParams);
    return this.filterParams?.tax_year ?? gp.tax_year ?? this.rowData?.TAX_YEAR;
  }

  private loadLookups(): void {
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api;
    if (!baseUrl) {
      return;
    }
    this.isLoadingLookups = true;
    this.loading = true;
    this.cdr.detectChanges();

    const taxYear = this.taxYear;
    const endLoad = (): void => {
      this.loading = false;
      this.isLoadingLookups = false;
      this.cdr.detectChanges();
    };

    this.lookupSub = this.service
      .getSourceCodes(baseUrl, taxYear)
      .pipe(
        switchMap((d1: any) => {
          this.sourceOptions = d1?.jsonObject || [];
          return this.service.getFtcCodes(baseUrl, taxYear);
        }),
        switchMap((d2: any) => {
          this.ftcOptions = d2?.jsonObject || [];
          return this.service.getPassiveCodes(baseUrl, taxYear);
        }),
        switchMap((d3: any) => {
          this.passiveOptions = d3?.jsonObject || [];
          return this.service.getSubfCodes(baseUrl, taxYear);
        }),
        switchMap((d4: any) => {
          this.subfOptions = d4?.jsonObject || [];
          return this.service.getFphciCodes(baseUrl, taxYear);
        }),
        switchMap((d5: any) => {
          this.fphciOptions = d5?.jsonObject || [];
          return this.service.get902Name(baseUrl, taxYear);
        })
      )
      .subscribe({
        next: (d6: any) => {
          this.name902Options = d6?.jsonObject || [];
          endLoad();
        },
        error: () => {
          endLoad();
        },
      });
  }

  updateBasket(): void {
    const s = this.selectedSource?.DISPLAY_NAME || '';
    const f = this.selectedFtc?.DISPLAY_NAME || '';
    const p = this.selectedPassive?.DISPLAY_NAME || '';
    const sub = this.selectedSubf?.DISPLAY_NAME || '';
    const fp = this.selectedFphci?.DISPLAY_NAME || '';
    this.basketName = [s, f, p, sub, fp].filter(Boolean).join(' - ');
  }

  isPassiveDisabled(): boolean {
    if (this.selectedFtc?.FTC === 'Passive') return false;
    this.selectedPassive = null;
    return true;
  }

  isFphciDisabled(): boolean {
    if (this.selectedSubf?.SUBF === 'FPHCI') return false;
    this.selectedFphci = null;
    return true;
  }

  save(): void {
    if (this.isSaveClicked) return;
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api;
    if (!baseUrl) return;
    this.isSaveClicked = true;
    const basketJson = {
      basket_name: this.basketName,
      source_id: this.selectedSource?.SOURCE_ID,
      source_percent: this.selectedSource?.SOURCE_PERCENT,
      ftc_id: this.selectedFtc?.FTC_ID,
      passive_id: this.selectedPassive?.PASSIVE_ID,
      subf_id: this.selectedSubf?.SUBF_ID,
      fphci_id: this.selectedFphci?.FPHCI_ID,
      Basket_Category: 'U',
      Basket_Status: 'I',
      from_year: this.taxYear,
      to_year: '9999',
      leid_902: this.selected902Name?.LEID_902,
      cdr_no_902: this.selected902Name?.CDR_NO_902
    };

    const globalParamsForSave = extractObject(this.globalService?.globalParams);

    this.service.saveBasket(
      baseUrl,
      basketJson,
      globalParamsForSave,
      this.filterParams || {},
      this.USER_SETTINGS?.user?.sso_id || this.globalService?.sso_id || ''
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        this.isSaveClicked = false;
        if (res?.errorMessage && res.errorMessage !== 'null') {
          this.snack.openSnackBar(res.errorMessage, 'gtw-snackbar--red');
        } else {
          this.snack.openSnackBar('The new Basket was successfully added', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.isSaveClicked = false;
        this.snack.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    });
  }

  reset(): void {
    this.selectedSource = null;
    this.selectedFtc = null;
    this.selectedPassive = null;
    this.selectedSubf = null;
    this.selectedFphci = null;
    this.selected902Name = null;
    this.basketName = '';
    this.cdr.detectChanges();
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
